import {
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import type { PaginationState, SortingState } from '@tanstack/react-table'
import { type ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'

import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import { InfoQuickView } from '@/components/ui/table/components/InfoQuickView.tsx'
import { TooltipWithList } from '@/components/ui/table/components/tooltip-with-list.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { type BadgeVariantsType, Tag } from '@/components/ui/tag.tsx'
import { $creatorId } from '@/models/creators.ts'
import type { MediaOption } from '@/modules/message/components/editor/fields/message-media-dropdown.tsx'
import { useMessageMediaQuery } from '@/modules/message/query/message-media-query-context.tsx'
import { massMessageService } from '@/modules/online-mass-messages/service/mass-message-service.ts'
import { processDataForMessageStatistics } from '@/modules/online-mass-messages/utils.ts'
import {
  TrackedFeatures,
  trackFeatureUsage,
} from '@/utils/feature-engagement.ts'

import { useMassMessageTable } from '../hooks/use-mass-message-table'
import type { BulkMessageResponseContent } from '../types'
import { ActionButton } from '../ui/action-button'
import { MassMessagesEmptyData } from '../ui/error/empty-data'
import { MassMessagesSkeleton } from '../ui/table-skeleton'
import { MassMessagesSettings } from './mass-messages-settings.tsx'

function buildColumns(
  checkIfSomethingIsRunning: () => boolean,
  mediaOptions: MediaOption[],
  creatorId: number,
): ColumnDef<BulkMessageResponseContent>[] {
  return [
    {
      accessorKey: 'message',
      header: 'Message',
      enableSorting: false,
      cell: ({ row }) => {
        const followUpMessages = row.original.message?.followUpEnabled
          ? row.original.followUpDto?.sequence?.messages
          : []

        return (
          <TooltipWithList
            messages={row.original.message?.currentSequence?.messages || []}
            mediaOptions={mediaOptions}
            followUpMessages={followUpMessages || []}
          />
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.message?.status

        let customClassName = ''
        let variant: BadgeVariantsType['variant']

        switch (status) {
          case 'Done':
            variant = 'success'
            break
          case 'Running':
            variant = 'success'
            break
          case 'Pending':
            variant = 'warning'
            break
          case 'Paused':
            variant = 'neutral'
            customClassName =
              'bg-secondary-50 before:bg-secondary-200 dark:bg-secondary-600'
            break
          default:
            variant = 'danger'
        }

        return (
          <Tag
            variant={variant}
            withDot
            className={customClassName}
          >
            {status === 'Pending' ? 'Planned' : status}
          </Tag>
        )
      },
    },
    {
      accessorKey: 'startsAt',
      header: 'Start Date',
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.original.message?.startsAt)
        const dateB = new Date(rowB.original.message?.startsAt)

        if (dateA < dateB) {
          return -1
        }

        if (dateA > dateB) {
          return 1
        }

        return 0
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {format(
            new Date(row.original.message?.startsAt),
            'LLL dd, y hh:mm a',
          )}
        </span>
      ),
    },
    {
      accessorKey: 'expiresAt',
      header: 'End Date',
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.original.message?.expiresAt)
        const dateB = new Date(rowB.original.message?.expiresAt)

        if (dateA < dateB) return -1
        if (dateA > dateB) return 1

        return 0
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">
          {format(
            new Date(row.original.message?.expiresAt),
            'LLL dd, y hh:mm a',
          )}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 70,
      header: () => <div className="flex w-full justify-end">Actions</div>,
      cell: ({ row }) => {
        const processData = async () => {
          const data =
            await massMessageService.getMassMessageStatisticsByMessageId({
              creatorId,
              messageId: row.original.message?.id,
            })

          return processDataForMessageStatistics(data, 'Online Mass Messages')
        }

        return (
          <div className="flex w-full justify-end gap-4">
            <InfoQuickView
              data={[]}
              fetchData={processData}
            />
            <ActionButton
              row={row}
              checkIfSomethingIsRunning={checkIfSomethingIsRunning}
            />
          </div>
        )
      },
    },
  ]
}

interface Props {}

export const MassMessagesTable: FC<Props> = () => {
  const creatorId = useUnit($creatorId)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const [sorting, setSorting] = useState<SortingState>([])

  const { data, isFetching } = useMassMessageTable({
    creatorId: creatorId!,
    page: pagination.pageIndex,
    size: pagination.pageSize,
    sortOption: sorting,
  })

  useEffect(() => {
    if (data?.totalElements) {
      trackFeatureUsage(TrackedFeatures.OnlineMassMessaging, creatorId)
    }
  }, [data, creatorId])

  const memoizedPaginationState = useMemo(
    () => ({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    }),
    [pagination.pageIndex, pagination.pageSize],
  )

  const renderEmpty = useCallback(
    () => (
      <EmptyTableContent>
        It looks like you don&#39;t have any messages created yet, click on the{' '}
        <span className="text-pointer text-primary-500">Add Message</span>{' '}
        button to do so.
      </EmptyTableContent>
    ),
    [],
  )

  const medias = useMessageMediaQuery()

  const mediaOptions = useMemo<MediaOption[]>(
    () =>
      medias.orElse([]).map(({ id, mediaAttributes }) => ({
        mediaId: id,
        name: mediaAttributes?.label ?? '-',
        sorting: 0,
      })),
    [medias],
  )

  const checkIfSomethingIsRunning = useCallback(() => {
    return data?.content.some((d) => d?.message?.status === 'Running') ?? false
  }, [data])

  const columns = useMemo(
    () => buildColumns(checkIfSomethingIsRunning, mediaOptions, creatorId!),
    [checkIfSomethingIsRunning, mediaOptions, creatorId],
  )

  const sortingChange = (sorting: SetStateAction<SortingState>) => {
    setSorting(sorting)
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    })
  }

  if (isFetching) {
    return <MassMessagesSkeleton />
  }

  if (!data || data.content.length === 0) {
    return <MassMessagesEmptyData />
  }

  return (
    <>
      <EmptyProvider render={renderEmpty}>
        <DataTable
          enablePagination
          enableSorting
          hasActionColumn
          paginationState={memoizedPaginationState}
          onPaginationChange={setPagination}
          sortingState={sorting}
          onSortingChange={sortingChange}
          columns={columns}
          data={data.content}
          pageCount={data.totalPages}
        />
      </EmptyProvider>

      <MassMessagesSettings />
    </>
  )
}
